import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Paper from "@mui/material/Paper";
import { IconButton, Skeleton, Stack, SxProps, Theme, Typography } from "@mui/material";
import { BasicTileConfig } from "src/components/cards/custom/SenecBasicTile/dataTypes";
import { ReactElement } from "react";
import { useHighlightedFont } from "src/hooks/useHighlightedFont";
import { useScreenSize } from "src/hooks/useScreenSize";

export interface BasicTileProps {
    tileConfig?: BasicTileConfig;
    isLoading: boolean;
    highlightLeft?: boolean;
    onClick?: () => void;
    interactiveIcon?: ReactElement;
    sx?: SxProps<Theme>;
}

/**
 * @deprecated use SenecTile instead
 */
export const BasicTile = ({ tileConfig, isLoading, highlightLeft = false, onClick, interactiveIcon, sx }: BasicTileProps) => {
    const { isMobile } = useScreenSize();
    const handleTileClick = () => {
        if (!onClick) return;
        onClick();
    };

    return isLoading || !tileConfig ? (
        <Skeleton data-testid="skeleton" variant="rounded" sx={{ borderRadius: "0.75rem" }} width="100%" height="200px" />
    ) : (
        <Paper
            onClick={() => handleTileClick()}
            data-testid="system-tile"
            variant="outlined"
            sx={{
                height: "100%",
                p: "1.5rem",
                borderRadius: "0.75rem",
                borderColor: "senecSecondary.neutralGray20",
                "&:hover": {
                    cursor: onClick && "pointer",
                },
                ...sx,
            }}
        >
            {isMobile ? (
                <MobileContent tileConfig={tileConfig} highlightLeft={highlightLeft} interactiveIcon={interactiveIcon} />
            ) : (
                <DesktopContent tileConfig={tileConfig} highlightLeft={highlightLeft} interactiveIcon={interactiveIcon} />
            )}
        </Paper>
    );
};

export interface TableProps {
    tileConfig: BasicTileConfig;
    highlightLeft: boolean;
    interactiveIcon?: ReactElement;
}

export const MobileContent = ({ tileConfig, highlightLeft, interactiveIcon }: TableProps) => {
    const { getHighlightedFont } = useHighlightedFont();

    return (
        <Stack spacing={"26px"}>
            <TileHeader title={tileConfig.title} interactiveIcon={interactiveIcon} />
            {tileConfig.data.map((elem) => (
                <Stack key={"tile-row-" + elem.label} spacing={"10px"} data-testid={"tile-row"}>
                    <Typography variant={getHighlightedFont(highlightLeft)}>{elem.label}</Typography>
                    <Typography variant={getHighlightedFont(!highlightLeft)} sx={{ wordWrap: "break-word" }}>
                        {elem.content}
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
};

export const TileHeader = ({ title, interactiveIcon }: { title?: string; interactiveIcon?: ReactElement }) => {
    if (!title) return <></>;

    return (
        <Stack direction={"row"} justifyContent={"space-between"} data-testid={"tile-header"}>
            <Typography variant="h4">{title}</Typography>
            {interactiveIcon && (
                <IconButton
                    disableRipple
                    data-testid={"interactive-icon"}
                    sx={{
                        position: "relative",
                        top: "-5px",
                        p: "4px 8px",
                        backgroundColor: "senecSecondary.neutralGray000",
                        borderRadius: "8px",
                        "&:hover": {
                            backgroundColor: "senecSecondary.neutralGray20",
                        },
                        "&:active": {
                            backgroundColor: "senecSecondary.neutralGray000",
                        },
                    }}
                >
                    {interactiveIcon}
                </IconButton>
            )}
        </Stack>
    );
};

const tableCellStyle = {
    verticalAlign: "baseline",
    wordWrap: "break-word",
    border: 0,
    p: 0,
};

export const DesktopContent = ({ tileConfig, highlightLeft, interactiveIcon }: TableProps) => {
    const { getHighlightedFont } = useHighlightedFont();

    return (
        <Stack spacing={3}>
            <TileHeader title={tileConfig.title} interactiveIcon={interactiveIcon} />
            <TableContainer>
                <Table aria-label="simple-table" size="small" sx={{ borderCollapse: "separate", borderSpacing: "0 1rem" }}>
                    <TableBody sx={{ p: 0 }}>
                        {tileConfig.data.map((elem) => (
                            <TableRow key={"tile-row-" + elem.label} sx={{ border: 0 }}>
                                <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{
                                        ...tableCellStyle,
                                        width: "150px",
                                    }}
                                >
                                    <Typography variant={getHighlightedFont(highlightLeft)}>{elem.label}</Typography>
                                </TableCell>
                                <TableCell
                                    sx={{
                                        ...tableCellStyle,
                                        verticalAlign: "baseline",
                                        wordBreak: "break-word",
                                    }}
                                >
                                    <Typography variant={getHighlightedFont(!highlightLeft)} ml={"0.5rem"}>
                                        {elem.content}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Stack>
    );
};
